import React from 'react';
import Head from 'next/head';
import { useTranslation } from 'react-i18next';

import ScreenTopBar from 'legacy/components/ScreenTopBar';
import FadeIn from 'legacy/common/components/transition/FadeIn';
import BlurredBackgroundSection from 'components/BlurredBackgroundSection';
import CenteredContainer from 'components/CenteredContainer';

import DashboardHeader from '../components/new/DashboardHeader';
import DashboardBottomBar from '../components/DashboardBottomBar';
import DashboardSkeleton from '../components/DashboardSkeleton';
import { useEventDashboard } from '../context';
import EventMenu from '../components/new/EventMenu';
import EventDashboardTotals from '../components/new/EventDashboardTotals';
import TicketingSection from '../components/new/TicketingSection';
import CampaignSection from '../components/new/CampaignSection';
import KeyMetricsSection from '../components/new/KeyMetricsSection';
import ManageGuestsSection from '../components/new/ManageGuestsSection';
import { saveAs } from 'file-saver';
import { useExportEventAnalytics } from 'legacy/events/hooks';

const NewEventDashboardContent = () => {
  const { t } = useTranslation(['events']);
  const {
    data: { event, eventAnalytics },
    goBack,
    toggleManageEventModal
  } = useEventDashboard();

  const { refetch: fetchExcelData, isLoading: isFetchingExportAnalytics } =
    useExportEventAnalytics(event?._id, {
      isEnabled: false
    });

  if (!event || !eventAnalytics) {
    return <DashboardSkeleton />;
  }

  const onClickExportAnalytics = async () => {
    const { data: rawData } = await fetchExcelData();
    const blob = new Blob([rawData], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8'
    });

    saveAs(blob, t('events:dashboard.content.fileName'));
  };

  return (
    <>
      <Head>
        <title>
          {t('events:dashboard.content.title', { title: event.title })}
        </title>
      </Head>
      <ScreenTopBar
        title={t('events:dashboard.content.eventDashboard')}
        onMenuClick={toggleManageEventModal}
        onBack={goBack}
        wideContent
      >
        {event.isDraft && (
          <div className="flex-1 justify-end flex pr-2">
            <div className="border-1 rounded-full px-3 items-center flex">
              {t('events:dashboard.content.draft')}
            </div>
          </div>
        )}
      </ScreenTopBar>
      <div className="md:pt-[46px] -mt-[41px] md:mt-0 pb-20">
        <div className="relative z-1">
          <FadeIn>
            <BlurredBackgroundSection imageUrl={event.photo?.url}>
              <CenteredContainer wideContent>
                <div className="pt-5 md:pt-11">
                  <DashboardHeader />
                  <EventMenu isOrganizer={event.isOrganizer} />
                </div>
              </CenteredContainer>
            </BlurredBackgroundSection>
            <CenteredContainer wideContent>
              <div className="pt-11 gap-11 flex flex-col">
                <EventDashboardTotals />
                <TicketingSection
                  event={event}
                  onClickExportAnalytics={onClickExportAnalytics}
                  isFetchingExportAnalytics={isFetchingExportAnalytics}
                />
                <CampaignSection
                  isOrganizer={event.isOrganizer}
                  onClickExportAnalytics={onClickExportAnalytics}
                  isFetchingExportAnalytics={isFetchingExportAnalytics}
                />
                <KeyMetricsSection />
                <ManageGuestsSection isOrganizer={event.isOrganizer} />
              </div>
            </CenteredContainer>
          </FadeIn>
        </div>
      </div>
      <DashboardBottomBar />
    </>
  );
};

export default NewEventDashboardContent;
